import { useRef, useEffect, useState } from "react";
import {
  Mail, Phone, DollarSign, ChevronUp, ChevronDown,
  MousePointer, Layers, X,
} from "lucide-react";
import "./OfferBuilderForm.css";

/* ── Step metadata ───────────────────────────────────────── */
const BUILTIN_META = {
  emailSignup: { label: "Email Signup",  desc: "Collect leads before sale",    Icon: Mail },
  callBooking: { label: "Call Booking",  desc: "Prospects book a discovery call", Icon: Phone },
  sale:        { label: "Sale",          desc: "Customer completes purchase",   Icon: DollarSign },
};

/* ── Tiny sub-components ─────────────────────────────────── */
const ArrowDown = () => (
  <div className="ob-arrow-down">
    <div className="ob-arrow-line" />
    <div className="ob-arrow-head" />
  </div>
);

/* The "Add step" insertion button */
const AddStepButton = ({ onClick, disabled, showPro }) => (
  <button
    type="button"
    className={`ob-add-step-btn ${(disabled || showPro) ? "ob-add-step-btn--disabled" : ""}`}
    onClick={(disabled || showPro) ? undefined : onClick}
    disabled={disabled || showPro}
  >
    + Add step
    {showPro && <span className="ob-pro-badge" style={{ marginLeft: 6 }}>PRO</span>}
  </button>
);

/* ── Step-type picker modal ──────────────────────────────── */
const StepTypeModal = ({
  onSelect,
  onClose,
  hasEmailSignup,
  hasCallBooking,
  hasSale,
  isProAccount,
  atLimit,
}) => {
  const options = [
    {
      key: "emailSignup",
      label: "Email Signup",
      desc: "Track when visitors opt in to your list",
      Icon: Mail,
      disabled: hasEmailSignup || atLimit || (!isProAccount && hasCallBooking),
      disabledReason: hasEmailSignup ? "Already added" : (!isProAccount && hasCallBooking) ? "Not available" : atLimit ? "Upgrade to add more steps" : null,
      pro: false,
    },
    {
      key: "callBooking",
      label: "Call Booking",
      desc: "Track when prospects book a discovery call",
      Icon: Phone,
      disabled: hasCallBooking || atLimit || (!isProAccount && hasEmailSignup),
      disabledReason: hasCallBooking ? "Already added" : (!isProAccount && hasEmailSignup) ? "Not available" : atLimit ? "Upgrade to add more steps" : null,
      pro: false,
    },
    {
      key: "custom",
      label: "Custom Step",
      desc: "Add any custom conversion point",
      Icon: Layers,
      disabled: !isProAccount || atLimit,
      disabledReason: !isProAccount ? null : atLimit ? "Upgrade to add more steps" : null,
      pro: !isProAccount,
    },
    ...(!hasSale ? [{
      key: "sale",
      label: "Sale",
      desc: "Customer completes purchase",
      Icon: DollarSign,
      disabled: false,
      disabledReason: null,
      pro: false,
    }] : []),
  ];

  return (
    <div className="ob-modal-overlay" onClick={onClose}>
      <div className="ob-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ob-modal-header">
          <p className="ob-modal-title">Add a step</p>
          <button className="ob-modal-close" onClick={onClose} type="button">
            <X size={16} />
          </button>
        </div>
        <p className="ob-modal-subtitle">Choose the type of step to add to your funnel</p>

        {atLimit && (
          <p className="ob-upgrade-hint">
            To add more than 2 steps, please upgrade to the pro plan.
          </p>
        )}

        <div className="ob-modal-options">
          {options.map(({ key, label, desc, Icon, disabled, disabledReason, pro }) => (
            <button
              key={key}
              type="button"
              className={`ob-modal-option ${disabled ? "ob-modal-option--disabled" : ""}`}
              onClick={disabled ? undefined : () => onSelect(key)}
              disabled={disabled}
            >
              <div className={`ob-modal-option-icon ${disabled ? "" : "ob-modal-option-icon--colored"}`}>
                <Icon size={16} />
              </div>
              <div className="ob-modal-option-text">
                <span className="ob-modal-option-label">
                  {label}
                  {pro && <span className="ob-pro-badge">PRO</span>}
                  {disabledReason && !pro && (
                    <span className="ob-already-badge">{disabledReason}</span>
                  )}
                </span>
                <span className="ob-modal-option-desc">{desc}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Main component ─────────────────────────────────────── */
export const OfferBuilderForm = ({
  steps,
  onStepsChange,
  offerName,
  onOfferNameChange,
  offerPrice,
  onOfferPriceChange,
  hasSecondPrice,
  onHasSecondPriceChange,
  secondPrice,
  onSecondPriceChange,
  hasThirdPrice,
  onHasThirdPriceChange,
  thirdPrice,
  onThirdPriceChange,
  customPrice,
  onCustomPriceChange,
  currency,
  onSubmit,
  isLoading,
  isProAccount,
  submitLabel = "Create Offer",
  websiteEditor,
  onWebsiteEditorChange,
  username,
  isOpen, // Add this prop
}) => {
  /* State for wizard flow */
  const [offerTypeSelected, setOfferTypeSelected] = useState(null);
  const [hasCallBookingToggle, setHasCallBookingToggle] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);

  /* Reset form when modal opens */
  useEffect(() => {
    if (isOpen) {
      setOfferTypeSelected(null);
      setHasCallBookingToggle(false);
      setAdvancedMode(false);
    }
  }, [isOpen]);

  /* Derived */
  const saleStep = steps.find((s) => s.type === "builtin" && s.key === "sale");
  const includeSale = !!saleStep;
  const intermediateSteps = steps.filter(
    (s) => !(s.type === "builtin" && s.key === "sale")
  );
  const hasEmailSignup = steps.some((s) => s.type === "builtin" && s.key === "emailSignup");
  const hasCallBooking = steps.some((s) => s.type === "builtin" && s.key === "callBooking");
  const atLimit = !isProAccount && intermediateSteps.length >= 2;

  // Shopify auto-treats the price as custom (revenue tracked from checkout).
  // Lovable and "Other" use a normal price input like any other offer.
  const isShopify = websiteEditor === 'shopify';
  const isLovable = websiteEditor === 'lovable';
  
  /* Modal state: null = closed, number = insert index */
  const [pendingInsert, setPendingInsert] = useState(null);

  /* Auto-focus new custom step name inputs */
  const customNameRefs = useRef([]);
  const customSteps = steps.filter((s) => s.type === "custom");
  useEffect(() => {
    if (customSteps.length > 0) {
      const lastRef = customNameRefs.current[customSteps.length - 1];
      if (lastRef) lastRef.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customSteps.length]);

  /* Update steps when offer type changes (non-advanced mode) */
  useEffect(() => {
    if (advancedMode) return;

    if (offerTypeSelected === 'paid') {
      onStepsChange([
        { type: 'builtin', key: 'sale', active: true }
      ]);
    } else if (offerTypeSelected === 'paidWithCall') {
      onStepsChange([
        { type: 'builtin', key: 'callBooking', active: true },
        { type: 'builtin', key: 'sale', active: true }
      ]);
    } else if (offerTypeSelected === 'email') {
      onStepsChange([
        { type: 'builtin', key: 'emailSignup', active: true }
      ]);
    }
  }, [offerTypeSelected, advancedMode]);

  /* ── Mutations ─────────────────────────────────────────── */
  // insertIdx is the actual index in the steps array to splice at
  const addStepAt = (insertIdx, stepKey) => {
    if (stepKey === "sale") {
      onStepsChange([...steps, { type: "builtin", key: "sale", active: true }]);
      setPendingInsert(null);
      return;
    }
    let newStep;
    if (stepKey === "custom") {
      newStep = { type: "custom", id: Date.now(), name: "", price: "", active: true };
    } else {
      newStep = { type: "builtin", key: stepKey, active: true };
    }
    const next = [...steps];
    next.splice(insertIdx, 0, newStep);
    onStepsChange(next);
    setPendingInsert(null);
  };

  const removeStep = (step) => {
    onStepsChange(steps.filter((s) => s !== step));
  };

  const updateCustomStep = (id, field, value) => {
    onStepsChange(
      steps.map((s) => (s.type === "custom" && s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const moveUp = (step) => {
    const idx = steps.indexOf(step);
    if (idx <= 0) return;
    const next = [...steps];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onStepsChange(next);
  };

  const moveDown = (step) => {
    const idx = steps.indexOf(step);
    if (idx >= steps.length - 1) return;
    const nextStep = steps[idx + 1];
    // Builtin steps (email/call) cannot move past Sale
    if (
      step.type === "builtin" &&
      nextStep?.type === "builtin" && nextStep?.key === "sale"
    ) return;
    const next = [...steps];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onStepsChange(next);
  };

  /* ── Validation ─────────────────────────────────────────── */
  const missingName  = !offerName.trim();
  const missingPrice = includeSale && !customPrice && !isShopify && !offerPrice;
  const disabledReason = missingName
    ? "Please enter a name for your offer"
    : missingPrice
    ? "Please enter the price of your offer"
    : null;
  const isDisabled = isLoading || !!disabledReason;

  /* ── Render helpers ─────────────────────────────────────── */
  let customRefIdx = 0;

  const renderStep = (step, actualIdx) => {
    const isFirst = actualIdx === 0;
    const nextStep = steps[actualIdx + 1];
    const isNextSale = nextStep?.type === "builtin" && nextStep?.key === "sale";
    const isAtEnd = actualIdx >= steps.length - 1;
    // Custom steps can move past Sale; builtin steps cannot
    const canMoveDown = step.type === "custom"
      ? !isAtEnd
      : !isNextSale && !isAtEnd;

    if (step.type === "custom") {
      const refIdx = customRefIdx++;
      return (
        <div key={step.id} className="ob-step-card ob-step-card--custom">
          <div className="ob-custom-step-left">
            <div className="ob-custom-step-icon">
              <Layers size={14} />
            </div>
            <input
              ref={(el) => (customNameRefs.current[refIdx] = el)}
              className="ob-custom-step-name-input"
              placeholder="Step name…"
              value={step.name}
              onChange={(e) => updateCustomStep(step.id, "name", e.target.value)}
            />
          </div>
          <div className="ob-custom-step-right">
            <div className="ob-custom-step-price-wrapper">
              <DollarSign size={13} className="ob-custom-step-price-icon" />
              <input
                className="ob-custom-step-price-input"
                type="number"
                placeholder="Price"
                value={step.price}
                onChange={(e) => updateCustomStep(step.id, "price", e.target.value)}
              />
            </div>
            <div className="ob-step-actions">
              <button
                type="button"
                className="ob-reorder-btn"
                onClick={() => moveUp(step)}
                disabled={isFirst}
                title="Move up"
              >
                <ChevronUp size={13} />
              </button>
              <button
                type="button"
                className="ob-reorder-btn"
                onClick={() => moveDown(step)}
                disabled={!canMoveDown}
                title="Move down"
              >
                <ChevronDown size={13} />
              </button>
              <button
                type="button"
                className="ob-step-remove-btn"
                onClick={() => removeStep(step)}
                title="Remove step"
              >
                <X size={13} />
              </button>
            </div>
          </div>
        </div>
      );
    }

    /* Builtin step card (email signup / call booking) */
    const { label, desc, Icon } = BUILTIN_META[step.key];
    return (
      <div key={step.key} className="ob-step-card ob-step-card--active">
        <div className="ob-funnel-step-left">
          <div className="ob-funnel-step-icon ob-funnel-step-icon--active">
            <Icon size={15} />
          </div>
          <div className="ob-funnel-step-text">
            <span className="ob-funnel-step-label">{label}</span>
            <span className="ob-funnel-step-desc">{desc}</span>
          </div>
        </div>
        <div className="ob-step-actions">
          <button
            type="button"
            className="ob-reorder-btn"
            onClick={() => moveUp(step)}
            disabled={isFirst}
            title="Move up"
          >
            <ChevronUp size={13} />
          </button>
          <button
            type="button"
            className="ob-reorder-btn"
            onClick={() => moveDown(step)}
            disabled={!canMoveDown}
            title="Move down"
          >
            <ChevronDown size={13} />
          </button>
          <button
            type="button"
            className="ob-step-remove-btn"
            onClick={() => removeStep(step)}
            title="Remove step"
          >
            <X size={13} />
          </button>
        </div>
      </div>
    );
  };

  /* ── JSX ─────────────────────────────────────────────────── */
  return (
    <div className="offer-builder-form">
      {/* Advanced mode toggle - always visible at top right after offer type selected */}
      {offerTypeSelected && offerTypeSelected !== 'complex' && (
        <div className="ob-advanced-toggle-top">
          <label className="ob-toggle-label">
            <span className="ob-toggle-text">Advanced mode</span>
            <span className="ob-toggle-switch">
              <input
                type="checkbox"
                className="ob-toggle-input"
                checked={advancedMode}
                onChange={(e) => setAdvancedMode(e.target.checked)}
              />
              <span className="ob-toggle-track">
                <span className="ob-toggle-thumb" />
              </span>
            </span>
          </label>
        </div>
      )}

      {/* Step 1: What do you want to track? - Always visible once selected */}
      <div className="ob-wizard-step">
        <h3 className="ob-wizard-title">What do you want to track?</h3>
        <div className="ob-offer-type-options">
          <button
            type="button"
            className={`ob-offer-type-option ${offerTypeSelected === 'paid' ? 'ob-offer-type-option--selected' : ''}`}
            onClick={() => {
              setOfferTypeSelected('paid');
              setHasCallBookingToggle(false);
              setAdvancedMode(false);
            }}
          >
            <div className="ob-offer-type-icon">
              <DollarSign size={24} />
            </div>
            <div className="ob-offer-type-label">Track sales</div>
          </button>

          <button
            type="button"
            className={`ob-offer-type-option ${offerTypeSelected === 'paidWithCall' ? 'ob-offer-type-option--selected' : ''}`}
            onClick={() => {
              setOfferTypeSelected('paidWithCall');
              setHasCallBookingToggle(true);
              setAdvancedMode(false);
            }}
          >
            <div className="ob-offer-type-icon">
              <Phone size={24} />
            </div>
            <div className="ob-offer-type-label">Track sales calls & sales</div>
          </button>

          <button
            type="button"
            className={`ob-offer-type-option ${offerTypeSelected === 'email' ? 'ob-offer-type-option--selected' : ''}`}
            onClick={() => {
              setOfferTypeSelected('email');
              setHasCallBookingToggle(false);
              setAdvancedMode(false);
            }}
          >
            <div className="ob-offer-type-icon">
              <Mail size={24} />
            </div>
            <div className="ob-offer-type-label">Track email signups</div>
          </button>

        </div>
      </div>

      {/* Show rest of form after offer type is selected */}
      {offerTypeSelected && !advancedMode && (
        <>
          {/* Offer name */}
          <div className="ob-field">
            <label className="ob-label" htmlFor="ob-offer-name">Name of your offer</label>
            <input
              id="ob-offer-name"
              className="ob-input"
              placeholder={isShopify ? "e.g., My Shopify Store" : isLovable ? "e.g., My Lovable Site" : "e.g., Mentorship, Masterclass, Newsletter"}
              value={offerName}
              onChange={(e) => onOfferNameChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !isDisabled && onSubmit()}
            />
          </div>

          {/* Price section (only for paid offers) */}
          {(offerTypeSelected === 'paid' || offerTypeSelected === 'paidWithCall') && (
            <div className="ob-prices-section">
              <div className="ob-field ob-price-field">
                <label className="ob-label" htmlFor="ob-offer-price">
                  Price Point {!customPrice && !isShopify && <span className="ob-currency-hint">({currency})</span>}
                </label>
                <div className="ob-price-input-wrapper">
                  {!isShopify ? (
                    <input
                      id="ob-offer-price"
                      className="ob-input"
                      placeholder="e.g., 497, 99"
                      value={offerPrice}
                      onChange={(e) => onOfferPriceChange(e.target.value)}
                      type="number"
                    />
                  ) : (
                    <div className="ob-custom-price-display">
                      <div className="ob-custom-price-badge">
                        <DollarSign size={14} />
                        <span>Custom / Varies</span>
                      </div>
                      <span className="ob-custom-price-hint">
                        Revenue will be tracked from Shopify checkout
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {!isShopify && !hasSecondPrice && (
                <button
                  type="button"
                  className={`ob-add-price-btn ${!isProAccount ? "ob-add-price-btn--locked" : ""}`}
                  onClick={() => isProAccount && onHasSecondPriceChange(true)}
                  disabled={!isProAccount}
                >
                  + Add another price
                  {!isProAccount && <span className="ob-pro-badge">PRO</span>}
                </button>
              )}

              {!isShopify && hasSecondPrice && (
                <>
                  <div className="ob-price-row">
                    <div className="ob-field ob-price-field">
                      <label className="ob-label" htmlFor="ob-second-price">
                        Price Point 2 <span className="ob-currency-hint">({currency})</span>
                      </label>
                      <input
                        id="ob-second-price"
                        className="ob-input"
                        placeholder="e.g., 997"
                        value={secondPrice}
                        onChange={(e) => onSecondPriceChange(e.target.value)}
                        type="number"
                      />
                    </div>
                    <button
                      type="button"
                      className="ob-price-remove-btn"
                      onClick={() => {
                        onHasSecondPriceChange(false);
                        onSecondPriceChange("");
                        onHasThirdPriceChange(false);
                        onThirdPriceChange("");
                      }}
                      title="Remove price point"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {!hasThirdPrice && (
                    <button
                      type="button"
                      className={`ob-add-price-btn ${!isProAccount ? "ob-add-price-btn--locked" : ""}`}
                      onClick={() => isProAccount && onHasThirdPriceChange(true)}
                      disabled={!isProAccount}
                    >
                      + Add another price
                      {!isProAccount && <span className="ob-pro-badge">PRO</span>}
                    </button>
                  )}

                  {hasThirdPrice && (
                    <div className="ob-price-row">
                      <div className="ob-field ob-price-field">
                        <label className="ob-label" htmlFor="ob-third-price">
                          Price Point 3 <span className="ob-currency-hint">({currency})</span>
                        </label>
                        <input
                          id="ob-third-price"
                          className="ob-input"
                          placeholder="e.g., 1997"
                          value={thirdPrice}
                          onChange={(e) => onThirdPriceChange(e.target.value)}
                          type="number"
                        />
                      </div>
                      <button
                        type="button"
                        className="ob-price-remove-btn"
                        onClick={() => {
                          onHasThirdPriceChange(false);
                          onThirdPriceChange("");
                        }}
                        title="Remove price point"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Submit */}
          <div
            className={`ob-submit-wrapper ${isDisabled ? "ob-submit-wrapper--disabled" : ""}`}
            data-tooltip={disabledReason}
          >
            <button className="ob-submit-btn" onClick={onSubmit} disabled={isDisabled}>
              {isLoading ? "Creating..." : submitLabel}
            </button>
          </div>

          {/* Website editor selector at the bottom */}
          {(offerTypeSelected === 'paid' || offerTypeSelected === 'paidWithCall' || offerTypeSelected === 'email') && (
            <>
              <div className="ob-website-editor-selector">
                <p className="ob-website-editor-label">Do you use one of these to build your website?</p>
                <select
                  className="ob-website-editor-select"
                  value={websiteEditor || 'other'}
                  onChange={(e) => onWebsiteEditorChange(e.target.value)}
                >
                  <option value="other">Other</option>
                  <option value="shopify">Shopify</option>
                  <option value="lovable">Lovable</option>
                </select>
              </div>

              {isShopify && (
                <div className="ob-shopify-note">
                  <p>
                    <strong>Note:</strong> You should make only one offer per Shopify store. All your products in the store will be tracked using this same offer.
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Advanced mode - show full funnel builder */}
      {offerTypeSelected && advancedMode && (
        <>

          {/* Offer name */}
          <div className="ob-field">
            <label className="ob-label" htmlFor="ob-offer-name">Name</label>
            <input
              id="ob-offer-name"
              className="ob-input"
              placeholder={isShopify ? "e.g., My Shopify Store" : isLovable ? "e.g., My Lovable Site" : "e.g., Mentorship, Masterclass, Newsletter"}
              value={offerName}
              onChange={(e) => onOfferNameChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !isDisabled && onSubmit()}
            />
          </div>

          {/* Price section (only for paid offers) */}
          {(offerTypeSelected === 'paid' || offerTypeSelected === 'paidWithCall') && (
            <div className="ob-prices-section">
              <div className="ob-field ob-price-field">
                <label className="ob-label" htmlFor="ob-offer-price">
                  Price Point {!customPrice && !isShopify && <span className="ob-currency-hint">({currency})</span>}
                </label>
                <div className="ob-price-input-wrapper">
                  {!customPrice && !isShopify ? (
                    <input
                      id="ob-offer-price"
                      className="ob-input"
                      placeholder="e.g., 497, 99"
                      value={offerPrice}
                      onChange={(e) => onOfferPriceChange(e.target.value)}
                      type="number"
                    />
                  ) : (
                    <div className="ob-custom-price-display">
                      <div className="ob-custom-price-badge">
                        <DollarSign size={14} />
                        <span>Custom / Varies</span>
                      </div>
                      <span className="ob-custom-price-hint">
                        Revenue will be tracked from Shopify checkout
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {!isShopify && (
                <div className="ob-custom-price-toggle">
                  <label className="ob-toggle-label" htmlFor="ob-custom-price-check">
                    <span className="ob-toggle-text">Price varies by customer</span>
                    <span className="ob-toggle-switch">
                      <input
                        id="ob-custom-price-check"
                        type="checkbox"
                        className="ob-toggle-input"
                        checked={customPrice}
                        onChange={(e) => {
                          onCustomPriceChange(e.target.checked);
                          if (e.target.checked) {
                            onOfferPriceChange("");
                          }
                        }}
                      />
                      <span className="ob-toggle-track">
                        <span className="ob-toggle-thumb" />
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {customPrice && !isShopify && (
                <div className="ob-custom-price-info">
                  <p className="ob-custom-price-info-text">
                    To track the price of each sale, the thank you page must have a URL parameter with name <code>rtprice</code>. For example: <code>https://example.com/thank-you?rtprice=200</code> will record the sale for $200. If this variable is not present, the price will not be tracked.
                  </p>
                </div>
              )}

              {!customPrice && !hasSecondPrice && !isShopify && (
                <button
                  type="button"
                  className={`ob-add-price-btn ${!isProAccount ? "ob-add-price-btn--locked" : ""}`}
                  onClick={() => isProAccount && onHasSecondPriceChange(true)}
                  disabled={!isProAccount}
                >
                  + Add another price
                  {!isProAccount && <span className="ob-pro-badge">PRO</span>}
                </button>
              )}

              {!customPrice && hasSecondPrice && !isShopify && (
                <>
                  <div className="ob-price-row">
                    <div className="ob-field ob-price-field">
                      <label className="ob-label" htmlFor="ob-second-price">
                        Price Point 2 <span className="ob-currency-hint">({currency})</span>
                      </label>
                      <input
                        id="ob-second-price"
                        className="ob-input"
                        placeholder="e.g., 997"
                        value={secondPrice}
                        onChange={(e) => onSecondPriceChange(e.target.value)}
                        type="number"
                      />
                    </div>
                    <button
                      type="button"
                      className="ob-price-remove-btn"
                      onClick={() => {
                        onHasSecondPriceChange(false);
                        onSecondPriceChange("");
                        onHasThirdPriceChange(false);
                        onThirdPriceChange("");
                      }}
                      title="Remove price point"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {!hasThirdPrice && (
                    <button
                      type="button"
                      className={`ob-add-price-btn ${!isProAccount ? "ob-add-price-btn--locked" : ""}`}
                      onClick={() => isProAccount && onHasThirdPriceChange(true)}
                      disabled={!isProAccount}
                    >
                      + Add another price
                      {!isProAccount && <span className="ob-pro-badge">PRO</span>}
                    </button>
                  )}

                  {hasThirdPrice && (
                    <div className="ob-price-row">
                      <div className="ob-field ob-price-field">
                        <label className="ob-label" htmlFor="ob-third-price">
                          Price Point 3 <span className="ob-currency-hint">({currency})</span>
                        </label>
                        <input
                          id="ob-third-price"
                          className="ob-input"
                          placeholder="e.g., 1997"
                          value={thirdPrice}
                          onChange={(e) => onThirdPriceChange(e.target.value)}
                          type="number"
                        />
                      </div>
                      <button
                        type="button"
                        className="ob-price-remove-btn"
                        onClick={() => {
                          onHasThirdPriceChange(false);
                          onThirdPriceChange("");
                        }}
                        title="Remove price point"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Funnel visual (advanced mode) ── */}
          <div className="ob-funnel">
            <p className="ob-funnel-heading">Build your funnel</p>

            {/* Click (fixed top node) */}
            <div className="ob-node ob-node--fixed">
              <MousePointer size={13} className="ob-click-icon" />
              <span>Click</span>
            </div>

            {/* Render all steps */}
            {steps.map((step, actualIdx) => {
              if (step.type === "builtin" && step.key === "sale") {
                return (
                  <div key="sale-block" className="ob-step-wrapper">
                    <ArrowDown />
                    {!isShopify && (
                      <AddStepButton
                        onClick={() => setPendingInsert(actualIdx)}
                        disabled={false}
                        showPro={!isProAccount && (hasEmailSignup || hasCallBooking) && !!saleStep}
                      />
                    )}
                    {!isShopify && <ArrowDown />}
                    <div className="ob-step-card ob-step-card--active ob-step-card--sale">
                      <div className="ob-funnel-step-left">
                        <div className="ob-funnel-step-icon ob-funnel-step-icon--active">
                          <DollarSign size={15} />
                        </div>
                        <div className="ob-funnel-step-text">
                          <span className="ob-funnel-step-label">Sale</span>
                          <span className="ob-funnel-step-desc">Customer completes purchase</span>
                        </div>
                      </div>
                      {steps.length > 1 && (
                        <button
                          type="button"
                          className="ob-step-remove-btn"
                          onClick={() => onStepsChange(steps.filter((s) => !(s.type === "builtin" && s.key === "sale")))}
                          title="Remove Sale step"
                        >
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div key={step.type === "custom" ? step.id : step.key} className="ob-step-wrapper">
                  <ArrowDown />
                  {renderStep(step, actualIdx)}
                </div>
              );
            })}

            {/* If no Sale step, show Add step at the end */}
            {!saleStep && !isShopify && (
              <>
                <ArrowDown />
                <AddStepButton
                  onClick={() => setPendingInsert(steps.length)}
                  disabled={false}
                  showPro={!isProAccount && (hasEmailSignup || hasCallBooking) && !!saleStep}
                />
              </>
            )}
          </div>

          {/* Submit */}
          <div
            className={`ob-submit-wrapper ${isDisabled ? "ob-submit-wrapper--disabled" : ""}`}
            data-tooltip={disabledReason}
          >
            <button className="ob-submit-btn" onClick={onSubmit} disabled={isDisabled}>
              {isLoading ? "Creating..." : submitLabel}
            </button>
          </div>

          {isShopify && (
            <div className="ob-shopify-note">
              <p>
                <strong>Note:</strong> You should make only one offer per Shopify store. All your products in the store will be tracked using this same offer.
              </p>
            </div>
          )}

          {/* Step-type modal */}
          {pendingInsert !== null && (
            <StepTypeModal
              onSelect={(key) => addStepAt(pendingInsert, key)}
              onClose={() => setPendingInsert(null)}
              hasEmailSignup={hasEmailSignup}
              hasCallBooking={hasCallBooking}
              hasSale={!!saleStep}
              isProAccount={isProAccount}
              atLimit={atLimit}
            />
          )}
        </>
      )}
    </div>
  );
};
